<!-- 
화면명 : SM303.vue
작성일 : 2026-10-05
수정일 : 2026-10-06
설명   : 수불관리 > 재고조회(영업사원용) [SM303]
호출ID : SERP\CF300PR\R028
-->
<template>
  <div class="relative bg-blueGray-100 p-2 flex flex-col h-full">
    <!-- 상단 타이틀 및 액션 버튼 (PC: 풀경로 breadcrumb, 모바일: 현재 메뉴명만 표시) -->
    <div class="flex justify-between items-center mb-2">
      <Breadcrumb />
      <div class="flex space-x-1 items-center flex-shrink-0 ml-auto xl:ml-0">
        <UCButton label="조회" width="72px" height="26px" icon="search" @click="btnSearchClick" />
        <UCButton label="Excel" width="72px" height="26px" icon="excel" @click="btnExcelClick" v-show="false" />
      </div>
    </div>

    <!-- 검색 조건 영역 (PC: 기존 ERP Table 유지, 모바일: 유연한 Flow 레이아웃, 우측 상단 접기 지원) -->
    <div class="bg-white border border-gray-300 shadow-xs mb-2 top-wrapper relative transition-all">
      <!-- 조회영역 상단 헤더 / 우측 상단 접기 버튼 -->
      <div class="flex justify-between items-center px-2 py-1 bg-gray-50 border-b border-gray-200 text-xs">
        <span class="font-semibold text-gray-700 flex items-center gap-1.5">
          <i class="fa-solid fa-filter text-blue-500 text-xs"></i>
          <span>조회 조건</span>
        </span>
        <!-- 오른쪽 위 접기/펼치기 버튼 -->
        <button
          type="button"
          @click="isSearchFolded = !isSearchFolded"
          class="flex items-center gap-1 text-[11px] text-gray-600 hover:text-blue-600 bg-white hover:bg-gray-100 border border-gray-300 rounded px-2 py-0.5 shadow-2xs focus:outline-none cursor-pointer transition-colors"
          title="조회 조건 접기/펼치기"
        >
          <i :class="isSearchFolded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'" class="text-[10px] text-blue-600"></i>
          <span>{{ isSearchFolded ? '조건 펼치기' : '조건 접기' }}</span>
        </button>
      </div>

      <!-- 접힘 상태가 아닐 때만 조건 표시 -->
      <div>
        <!-- [PC 모드] 기존 ERP Table 스타일 -->
        <table v-show="!isSearchFolded" class="hidden xl:table w-full border-collapse text-xs">
          <tbody>
            <!-- 1행: 사업소, 재고처 (+예약재고포함), 상품분류, 상품코드/명 -->
            <tr class="border-b border-gray-300">
              <!-- 사업소 (@CGWNGU) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 w-16 text-center whitespace-nowrap">
                사업소
              </th>
              <td class="border-r border-gray-300 px-2 py-1 bg-white w-28">
                <select
                  v-model="searchParams.cgwngu"
                  class="w-full h-6 px-1.5 text-xs text-gray-800 border border-gray-300 rounded-none bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option v-for="b in bizpList" :key="b.code" :value="b.code">
                    {{ b.name }}
                  </option>
                </select>
              </td>

              <!-- 재고처 (@CJCODE) + 예약재고포함 (@ADDSUNJG) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 w-16 text-center whitespace-nowrap">
                재고처
              </th>
              <td class="border-r border-gray-300 px-2 py-1 bg-white w-56">
                <div class="flex items-center gap-3">
                  <select
                    v-model="searchParams.cjcode"
                    class="flex-1 h-6 px-1.5 text-xs text-gray-800 border border-gray-300 rounded-none bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option v-for="w in warehouseList" :key="w.CMCD" :value="w.CMCD">
                      {{ w.CMCD_NAME }}
                    </option>
                  </select>
                  <label class="inline-flex items-center gap-1 cursor-pointer select-none text-gray-700 font-medium whitespace-nowrap">
                    <input
                      type="checkbox" disabled
                      v-model="searchParams.addsunjg"
                      class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                    />
                    <span>예약재고포함</span>
                  </label>
                </div>
              </td>

              <!-- 상품분류 (@MIDDLECLS) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 w-20 text-center whitespace-nowrap">
                상품분류
              </th>
              <td class="border-r border-gray-300 px-2 py-1 bg-white w-48 relative" ref="categoryDropdownRef">
                <!-- 멀티선택 트리거 박스 -->
                <div
                  @click="toggleCategoryDropdown"
                  class="w-full h-6 px-1.5 text-xs text-gray-800 border border-gray-300 bg-white flex items-center justify-between cursor-pointer select-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  :title="selectedCategoryTitle"
                >
                  <span class="truncate" :class="{ 'text-gray-400': searchParams.middlecls.length === 0 }">
                    {{ selectedCategoryText }}
                  </span>
                  <span class="text-[9px] text-gray-500 ml-1 transition-transform inline-block" :class="{ 'rotate-180': isCategoryDropdownOpen }">▼</span>
                </div>

                <!-- 멀티선택 드롭다운 메뉴 -->
                <div
                  v-if="isCategoryDropdownOpen"
                  class="absolute top-full left-0 mt-0.5 w-60 bg-white border border-gray-300 shadow-lg z-50 text-xs rounded-none max-h-60 overflow-y-auto"
                >
                  <div class="px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex items-center justify-between sticky top-0 z-10">
                    <label class="inline-flex items-center gap-1.5 cursor-pointer font-semibold text-gray-700 select-none">
                      <input
                        type="checkbox"
                        v-model="isAllCategorySelected"
                        class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                      />
                      <span>전체선택 ({{ categoryList.length }})</span>
                    </label>
                    <button
                      type="button"
                      @click.stop="searchParams.middlecls = []"
                      class="text-[11px] text-gray-500 hover:text-red-600 underline cursor-pointer"
                    >
                      초기화
                    </button>
                  </div>
                  <div class="py-1">
                    <label
                      v-for="cat in categoryList"
                      :key="cat.CMCD"
                      class="flex items-center gap-2 px-2.5 py-1 hover:bg-blue-50 cursor-pointer select-none text-gray-800"
                    >
                      <input
                        type="checkbox"
                        :value="cat.CMCD"
                        v-model="searchParams.middlecls"
                        class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                      />
                      <span class="truncate">{{ cat.CMCD_NAME }}</span>
                    </label>
                    <div v-if="categoryList.length === 0" class="text-center py-2 text-gray-400 text-xs">
                      데이터가 없습니다.
                    </div>
                  </div>
                </div>
              </td>

              <!-- 상품코드 (@CPUMNM) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 w-24 text-center whitespace-nowrap">
                상품코드/명
              </th>
              <td class="px-2 py-1 bg-white">
                <input
                  type="text"
                  v-model="searchParams.cpumnm"
                  placeholder=""
                  class="w-full h-6 px-2 text-xs text-gray-800 border border-gray-300 rounded-none bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  @keyup.enter="btnSearchClick"
                />
              </td>
            </tr>

            <!-- 2행: 원산지 (@ORIGIN), 규격 (@LEN6, @LEN8, @LEN10, @LEN12, @LENETC) -->
            <tr>
              <!-- 원산지 (@ORIGIN) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 text-center whitespace-nowrap">
                원산지
              </th>
              <td colspan="3" class="border-r border-gray-300 px-3 py-1 bg-white">
                <div class="flex items-center gap-5 text-gray-700">
                  <label
                    v-for="item in originOptions"
                    :key="item.value"
                    class="inline-flex items-center gap-1 cursor-pointer select-none text-xs"
                  >
                    <input
                      type="checkbox"
                      v-model="searchParams.origins"
                      :value="item.value"
                      class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                    />
                    <span>{{ item.label }}</span>
                  </label>
                </div>
              </td>

              <!-- 규격 (@LEN6, @LEN8, @LEN10, @LEN12, @LENETC) -->
              <th class="bg-[#f7f5ee] border-r border-gray-300 px-2 py-1.5 font-semibold text-gray-700 text-center whitespace-nowrap">
                규격
              </th>
              <td colspan="3" class="px-3 py-1 bg-white">
                <div class="flex items-center gap-5 text-gray-700">
                  <label
                    v-for="item in specOptions"
                    :key="item.value"
                    class="inline-flex items-center gap-1 cursor-pointer select-none text-xs"
                  >
                    <input
                      type="checkbox"
                      v-model="searchParams.specs"
                      :value="item.value"
                      class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                    />
                    <span>{{ item.label }}</span>
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- [모바일 모드] Flow 형식으로 자연스럽게 행수가 조절되는 검색 영역 -->
        <div class="xl:hidden p-2 text-xs flex flex-wrap gap-2 items-center">

          <!-- 1행: 상품코드/명 (아래 줄로 내려서 w-full 차지) -->
          <div class="flex items-center gap-1.5 w-full">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-1 font-semibold text-gray-700 whitespace-nowrap">상품코드/명</span>
            <input
              type="text"
              v-model="searchParams.cpumnm"
              placeholder="품목코드 또는 품명 입력"
              class="flex-1 h-7 px-2 text-xs text-gray-800 border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              @keyup.enter="btnSearchClick"
            />
          </div>

          <!-- 2행: 사업소, 상품분류 (나란히 배치) -->
          <div  v-show="!isSearchFolded" class="flex items-center gap-1.5 flex-1 min-w-[130px]">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-1 font-semibold text-gray-700 whitespace-nowrap">사업소</span>
            <select
              v-model="searchParams.cgwngu"
              class="flex-1 h-7 px-1.5 text-xs text-gray-800 border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option v-for="b in bizpList" :key="b.code" :value="b.code">
                {{ b.name }}
              </option>
            </select>
          </div>

          <div  v-show="!isSearchFolded" class="flex items-center gap-1.5 flex-1 min-w-[130px] relative" ref="categoryDropdownMobileRef">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-1 font-semibold text-gray-700 whitespace-nowrap">상품분류</span>
            <div
              @click="toggleCategoryDropdown"
              class="flex-1 h-7 px-2 text-xs text-gray-800 border border-gray-300 bg-white flex items-center justify-between cursor-pointer select-none focus:outline-none"
              :title="selectedCategoryTitle"
            >
              <span class="truncate" :class="{ 'text-gray-400': searchParams.middlecls.length === 0 }">
                {{ selectedCategoryText }}
              </span>
              <span class="text-[9px] text-gray-500 ml-1 transition-transform inline-block" :class="{ 'rotate-180': isCategoryDropdownOpen }">▼</span>
            </div>

            <!-- 모바일 멀티선택 드롭다운 메뉴 -->
            <div
              v-if="isCategoryDropdownOpen"
              class="absolute top-full left-0 mt-0.5 w-60 bg-white border border-gray-300 shadow-lg z-50 text-xs rounded-none max-h-60 overflow-y-auto"
            >
              <div class="px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex items-center justify-between sticky top-0 z-10">
                <label class="inline-flex items-center gap-1.5 cursor-pointer font-semibold text-gray-700 select-none">
                  <input
                    type="checkbox"
                    v-model="isAllCategorySelected"
                    class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                  />
                  <span>전체선택 ({{ categoryList.length }})</span>
                </label>
                <button
                  type="button"
                  @click.stop="searchParams.middlecls = []"
                  class="text-[11px] text-gray-500 hover:text-red-600 underline cursor-pointer"
                >
                  초기화
                </button>
              </div>
              <div class="py-1">
                <label
                  v-for="cat in categoryList"
                  :key="cat.CMCD"
                  class="flex items-center gap-2 px-2.5 py-1 hover:bg-blue-50 cursor-pointer select-none text-gray-800"
                >
                  <input
                    type="checkbox"
                    :value="cat.CMCD"
                    v-model="searchParams.middlecls"
                    class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                  />
                  <span class="truncate">{{ cat.CMCD_NAME }}</span>
                </label>
                <div v-if="categoryList.length === 0" class="text-center py-2 text-gray-400 text-xs">
                  데이터가 없습니다.
                </div>
              </div>
            </div>
          </div>

          <!-- 3행: 재고처 + 예약포함 (아래 줄로 내려서 w-full 차지) -->
          <div  v-show="!isSearchFolded" class="flex items-center gap-1.5 w-full">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-1 font-semibold text-gray-700 whitespace-nowrap">재고처</span>
            <select
              v-model="searchParams.cjcode"
              class="flex-1 h-7 px-1.5 text-xs text-gray-800 border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option v-for="w in warehouseList" :key="w.CMCD" :value="w.CMCD">
                {{ w.CMCD_NAME }}
              </option>
            </select>
            <label class="inline-flex items-center gap-1 text-[11px] text-gray-700 font-medium whitespace-nowrap cursor-pointer ml-1">
              <input
                type="checkbox"
                v-model="searchParams.addsunjg"
                class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
              />
              <span>예약포함</span>
            </label>
          </div>

          <!-- 4행: 원산지 -->
          <div  v-show="!isSearchFolded" class="flex items-center gap-2 w-full pt-1.5 border-t border-gray-200 flex-wrap">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-0.5 font-semibold text-gray-700 whitespace-nowrap">원산지</span>
            <div class="flex items-center gap-3 flex-wrap text-gray-700">
              <label
                v-for="item in originOptions"
                :key="item.value"
                class="inline-flex items-center gap-1 cursor-pointer select-none text-xs"
              >
                <input
                  type="checkbox"
                  v-model="searchParams.origins"
                  :value="item.value"
                  class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                />
                <span>{{ item.label }}</span>
              </label>
            </div>
          </div>

          <!-- 5행: 규격 -->
          <div  v-show="!isSearchFolded" class="flex items-center gap-2 w-full pt-1.5 border-t border-gray-200 flex-wrap">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-0.5 font-semibold text-gray-700 whitespace-nowrap">규격</span>
            <div class="flex items-center gap-3 flex-wrap text-gray-700">
              <label
                v-for="item in specOptions"
                :key="item.value"
                class="inline-flex items-center gap-1 cursor-pointer select-none text-xs"
              >
                <input
                  type="checkbox"
                  v-model="searchParams.specs"
                  :value="item.value"
                  class="form-checkbox h-3.5 w-3.5 text-blue-600 rounded-none border-gray-400"
                />
                <span>{{ item.label }}</span>
              </label>
            </div>
          </div>

          <!-- 6행: 정렬선택 (모바일 전용: 규격 조회조건 바로 아래 배치) -->
          <div v-show="!isSearchFolded" class="flex items-center gap-2 w-full pt-1.5 border-t border-gray-200 flex-wrap">
            <span class="bg-[#f7f5ee] border border-gray-300 px-2 py-0.5 font-semibold text-gray-700 whitespace-nowrap">정렬선택</span>
            <div class="flex items-center gap-2.5 flex-wrap text-gray-700">
              <label class="inline-flex items-center gap-1 cursor-pointer select-none text-xs">
                <input
                  type="radio"
                  v-model="sortOrder"
                  value="A"
                  class="form-radio text-blue-600 h-3.5 w-3.5"
                  @change="onSortChange"
                />
                <span>상품명, 규격, 원산지순</span>
              </label>
              <label class="inline-flex items-center gap-1 cursor-pointer select-none text-xs">
                <input
                  type="radio"
                  v-model="sortOrder"
                  value="B"
                  class="form-radio text-blue-600 h-3.5 w-3.5"
                  @change="onSortChange"
                />
                <span>상품명, 원산지, 규격순</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 그리드 상단 툴바 / 옵션 (모바일에서는 2줄 표현: 1줄: 재고현황 내역, 2줄: 정렬선택) -->
    <div class="flex flex-col xl:flex-row xl:justify-between xl:items-center my-1.5 xl:my-1 text-xs gap-1.5">
      <!-- 1줄: 재고현황 내역 타이틀 (서브 섹션 타이틀: text-xs로 컴팩트하게 표현) -->
      <div class="flex items-center justify-between xl:justify-start font-semibold text-gray-700 text-xs">
        <div class="flex items-center">
          <span class="w-2 h-2 bg-amber-500 mr-1.5 inline-block rounded-xs shadow-xs"></span>
          <span>재고현황 내역</span>
        </div>
      </div>

      <!-- 2줄: 정렬선택 및 엑셀다운로드 옵션 (모바일: 2번째 줄, PC: 우측) -->
      <div class="hidden flex flex-wrap items-center gap-3 xl:gap-6 text-xs text-gray-700 font-medium bg-gray-50 xl:bg-transparent p-1.5 xl:p-0 rounded border border-gray-200 xl:border-none">
        <!-- 엑셀다운로드시 라디오 버튼 -->
        <!-- <div class="flex items-center gap-2">
          <span class="text-red-600 font-bold text-[11px]">▶</span>
          <span class="font-semibold text-gray-800 whitespace-nowrap">엑셀다운로드시 :</span>
          <div class="flex items-center gap-2">
            <label class="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="excelExportLevel" value="1" class="form-radio text-blue-600 h-3.5 w-3.5" />
              <span>1단</span>
            </label>
            <label class="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="excelExportLevel" value="2" class="form-radio text-blue-600 h-3.5 w-3.5" />
              <span>2단</span>
            </label>
            <label class="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="excelExportLevel" value="3" class="form-radio text-blue-600 h-3.5 w-3.5" />
              <span>3단</span>
            </label>
          </div>
        </div> -->

        <!-- 정렬선택 라디오 버튼 (@SORTGBN : PC 화면에서만 표시, 모바일은 조회조건 내 표시) -->
        <div class="hidden xl:flex items-center gap-2 flex-wrap">
          <span class="text-red-600 font-bold text-[11px]">▶</span>
          <span class="font-semibold text-gray-800 whitespace-nowrap">정렬선택 :</span>
          <div class="flex items-center gap-2.5 flex-wrap">
            <label class="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="sortOrder" value="A" class="form-radio text-blue-600 h-3.5 w-3.5" @change="onSortChange" />
              <span>상품명, 규격, 원산지순</span>
            </label>
            <label class="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="sortOrder" value="B" class="form-radio text-blue-600 h-3.5 w-3.5" @change="onSortChange" />
              <span>상품명, 원산지, 규격순</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- 그리드 영역 -->
    <div class="mt-1 flex-1 relative">
      <UCGrid
        ref="refUcGrid"
        :data="arrGridData"
        :columns="columns"
        :options="gridOptions"
        :isRowNumber="false"
        :selectable="true"
        :pagination="true"
        :paginationSize="100"
        autoHeight
        :excludeSelectors="['.top-wrapper']"
        :heightOffset="24"
        @row-selected="handleSelection"
      />
    </div>

    <!-- 하단 상태바 (컴팩트 슬림 바) -->
    <div class="mt-0.5 px-0.5 text-[11px] text-gray-600 font-medium flex items-center justify-between">
      <span>Result(s): <span class="font-bold text-blue-700">{{ totalResultCount }}</span></span>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { notify } from "@kyvg/vue3-notification";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

import Breadcrumb from "@/components/Common/Breadcrumb.vue";
import UCGrid from "@/components/UserControl/UCGrid.vue";
import UCButton from "@/components/UserControl/UCButton.vue";
import { apiExecuteDataSet } from "@/api/SCM/common";
import envs from "@/envs";

const refUcGrid = ref(null);
// 조회 조건 접기/펼치기 상태: 모바일/가로모드/태블릿(<1280px)은 기본 접힘(true), PC(>=1280px)는 기본 펼침(false)
const isSearchFolded = ref(typeof window !== "undefined" ? window.innerWidth < 1280 : false);

const handleResize = () => {
  if (typeof window !== "undefined") {
    // 1280px 미만(모바일/태블릿/가로모드)에서는 기본 접힘
    isSearchFolded.value = window.innerWidth < 1280;
  }
};

onMounted(() => {
  if (typeof window !== "undefined") {
    isSearchFolded.value = window.innerWidth < 1280;
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("orientationchange", handleResize);
  }
});

// 조회 조건 접힘/펼침 변경 시 그리드 높이 직접 재계산 (이벤트 디스패치 루프 완전 차단)
watch(isSearchFolded, () => {
  nextTick(() => {
    refUcGrid.value?.calcGridHeight?.();
  });
});

// 검색 조건 모델 (SP 파라미터 매핑)
const searchParams = reactive({
  cwcomp: "S000", // 회사코드 (@CWCOMP)
  cgwngu: "S000", // 사업소 (@CGWNGU)
  cjcode: "G001", // 재고처 (@CJCODE)
  addsunjg: false, // 예약재고포함 (@ADDSUNJG)
  cpumnm: "",  // 상품코드/명 (@CPUMNM)
  middlecls: ["H", "G", "I", "D", "A"], // 상품분류 (@MIDDLECLS) 멀티선택 배열
  origins: ["국산", "수입", "일산", "대만산", "기타"], // 원산지 (@ORIGIN)
  specs: ["10M", "12M"],                // 규격 (@LEN6, @LEN8, @LEN10, @LEN12, @LENETC)
});

// 회사 목록 (@CWCOMP)
const companyList = ref([
  { code: "S000", name: "삼우강업" }
]);

// 사업소 목록 (@CGWNGU)
const bizpList = ref([
  { code: "S000", name: "삼우강업" }
]);

// 재고처 목록 (@CJCODE)
const warehouseList = ref([]);

// 상품분류 목록 (@MIDDLECLS) 
const categoryList = ref([]);

// 상품분류 멀티선택 드롭다운 제어
const isCategoryDropdownOpen = ref(false);
const categoryDropdownRef = ref(null);
const categoryDropdownMobileRef = ref(null);

function toggleCategoryDropdown() {
  isCategoryDropdownOpen.value = !isCategoryDropdownOpen.value;
}

function closeCategoryDropdown(e) {
  const inDesktop =
    categoryDropdownRef.value && categoryDropdownRef.value.contains(e.target);
  const inMobile =
    categoryDropdownMobileRef.value &&
    categoryDropdownMobileRef.value.contains(e.target);

  if (!inDesktop && !inMobile) {
    isCategoryDropdownOpen.value = false;
  }
}

// 선택된 상품분류 표시 텍스트
const selectedCategoryText = computed(() => {
  if (!searchParams.middlecls || searchParams.middlecls.length === 0) {
    return "<선택>";
  }
  if (
    categoryList.value.length > 0 &&
    searchParams.middlecls.length === categoryList.value.length
  ) {
    return `전체 (${categoryList.value.length})`;
  }

  const selectedNames = categoryList.value
    .filter((cat) => searchParams.middlecls.includes(cat.CMCD))
    .map((cat) => cat.CMCD_NAME || cat.CMCD);

  if (selectedNames.length === 1) {
    return selectedNames[0];
  }
  return `${selectedNames[0]} 외 ${selectedNames.length - 1}건`;
});

// 툴팁용 전체 선택 이름
const selectedCategoryTitle = computed(() => {
  if (!searchParams.middlecls || searchParams.middlecls.length === 0) {
    return "";
  }
  return categoryList.value
    .filter((cat) => searchParams.middlecls.includes(cat.CMCD))
    .map((cat) => cat.CMCD_NAME || cat.CMCD)
    .join(", ");
});

// 전체선택 체크박스 연동
const isAllCategorySelected = computed({
  get() {
    return (
      categoryList.value.length > 0 &&
      searchParams.middlecls.length === categoryList.value.length
    );
  },
  set(val) {
    if (val) {
      searchParams.middlecls = categoryList.value.map((cat) => cat.CMCD);
    } else {
      searchParams.middlecls = [];
    }
  },
});

// 원산지 체크박스 옵션 (@ORIGIN)
const originOptions = [
  { label: "국산", value: "국산" },
  { label: "수입산", value: "수입" },
  { label: "일산", value: "일산" },
  { label: "대만산", value: "대만산" },
  { label: "기타", value: "기타" },
];

// 규격 체크박스 옵션 (@LEN6, @LEN8, @LEN10, @LEN12, @LENETC)
const specOptions = [
  { label: "6M", value: "6M" },
  { label: "8M", value: "8M" },
  { label: "10M", value: "10M" },
  { label: "12M", value: "12M" },
  { label: "기타", value: "기타" },
];

// 엑셀다운로드 단수 (기본 3단)
const excelExportLevel = ref("3");

// 정렬선택 (@SORTGBN : "A"=상품명, 규격, 원산지순 / "B"=상품명, 원산지, 규격순)
const sortOrder = ref("A");

// 총 건수
const totalResultCount = ref(0);

// 그리드 컬럼 정의 (요청된 필드 매핑)
const columns = [
  {
    title: "코드",
    field: "CPUMCD",
    width: 70,
    hozAlign: "center",
    headerHozAlign: "center",
    headerSort: false,
    frozen: true,
  },
  {
    title: "상품명",
    field: "CPUMNM",
    width: 120,
    hozAlign: "left",
    headerHozAlign: "center",
    headerSort: false,
  },
  {
    title: "재질",
    field: "CJEJIL",
    width: 120,
    hozAlign: "left",
    headerHozAlign: "center",
    headerSort: false,
  },
  {
    title: "원산지",
    field: "CORIGINNM",
    width: 80,
    hozAlign: "center",
    headerHozAlign: "center",
    headerSort: false,
  },
  {
    title: "규격",
    field: "CSTDS",
    width: 150,
    hozAlign: "left",
    headerHozAlign: "center",
    headerSort: false,
  },
  {
    title: "재고매수",
    field: "CJGMST",
    width: 80,
    hozAlign: "right",
    headerSort: false,
    headerHozAlign: "center",
    formatter: "money",
    formatterParams: {
      thousand: ",",
      precision: 0,
    },
  },
  {
    title: "재고중량",
    field: "CJGJRT",
    headerSort: false,
    width: 100,
    hozAlign: "right",
    headerHozAlign: "center",
    formatter: "money",
    formatterParams: {
      thousand: ",",
      precision: 0,
    },
  },
  {
    title: "재고처",
    field: "CJNAME",
    width: 80,
    hozAlign: "center",
    headerHozAlign: "center",
    headerSort: false,
    
  },
];

// 그리드 옵션 (합계 행 스타일링 등)
const gridOptions = {
  headerSort: true,
  selectableCheck: (row) => {
    const data = row.getData();
    return !data.IS_TOTAL && data.CPUMCD !== "전 체";
  },
  rowFormatter: function (row) {
    const data = row.getData();
    const element = row.getElement();

    // 상단 '전 체' 합계 행 강조
    const isTotalRow =
      data.CPUMCD === "전 체" ||
      data.IS_TOTAL ||
      (data.CPUMCD && data.CPUMCD.includes("총계"));

    const bg = isTotalRow ? "#fff5f5" : "";
    const color = isTotalRow ? "#b91c1c" : "";
    const weight = isTotalRow ? "bold" : "";

    element.style.backgroundColor = bg;
    element.style.fontWeight = weight;
    element.style.color = color;

    // 고정(frozen) 컬럼 셀에도 배경색 및 폰트 스타일 동기화
    const frozenCells = element.querySelectorAll(".tabulator-cell.tabulator-frozen");
    frozenCells.forEach((cell) => {
      cell.style.backgroundColor = bg;
      cell.style.fontWeight = weight;
      cell.style.color = color;
    });
  },
};

// 그리드 원본 데이터 및 바인딩 데이터
const rawDataList = ref([]);
const arrGridData = ref([]);

onMounted(async () => {
  document.addEventListener("click", closeCategoryDropdown);
  await SetCondition();
  loadData();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", closeCategoryDropdown);
});

async function SetCondition() {
  const params = {
    ID: "SERP\\T_COMMON\\DDLKEYBINDING",
    Parameter: {
      "@COMP": "S000",
      "@BIZP": "S000",
      "@CODEID": "STKHOUSE,MIDDLECLS"
    },
  };
  const res = await Promise.all([apiExecuteDataSet(params)]);
  console.log(res[0]._Result)
  warehouseList.value = res[0]._Result[0];
  categoryList.value = res[0]._Result[1]; 
}

// 원산지 파라미터 값 추출 (전체 선택/미선택 시 "" 전달)
function getOriginParam() {
  return (searchParams.origins.includes('국산')?'Y':'N')
  + (searchParams.origins.includes('수입')?'Y':'N')
  + (searchParams.origins.includes('일산')?'Y':'N')
  + (searchParams.origins.includes('대만산')?'Y':'N')
  + (searchParams.origins.includes('기타')?'Y':'N');
}
// 데이터 조회 함수 (SERP\CF300PR\R028 호출)
async function loadData() {
  if (categoryList.value.length === 0) {
    await SetCondition();
  }

  const originParam = getOriginParam();
  const middleclsParam = Array.isArray(searchParams.middlecls)
    ? searchParams.middlecls.join(",")
    : (searchParams.middlecls || "");
  const searchParam = {
    ID: "SERP\\CF300PR\\R028",
    Parameter: {
      "@CWCOMP": 'S000',
      "@CGWNGU": 'S000',
      "@CJCODE": searchParams.cjcode || "",
      "@ADDSUNJG": searchParams.addsunjg ? "Y" : "N",
      "@CPUMNM": searchParams.cpumnm?.trim() || "",
      "@MIDDLECLS": middleclsParam,
      "@ORIGIN": originParam,
      "@LEN6": searchParams.specs.includes("6M") ? "Y" : "N",
      "@LEN8": searchParams.specs.includes("8M") ? "Y" : "N",
      "@LEN10": searchParams.specs.includes("10M") ? "Y" : "N",
      "@LEN12": searchParams.specs.includes("12M") ? "Y" : "N",
      "@LENETC": searchParams.specs.includes("기타") ? "Y" : "N",
      "@SORTGBN": sortOrder.value,
    },
  };

  console.log('searchParam',searchParam)
  try {
    const res = await apiExecuteDataSet(searchParam);
    if (res && res._Result && Array.isArray(res._Result[0])) {
      rawDataList.value = res._Result[0];
    } else {
      rawDataList.value = [];
    }
  } catch (error) {
    console.error("데이터 조회 실패:", error);
  }

  applyFilterAndSort();
}

// 필터링, 정렬 및 합계 행 처리
function applyFilterAndSort() {
  let list = [...rawDataList.value];

  // 정렬 순서 적용 (클라이언트 보조 정렬)
  // list.sort((a, b) => {
  //   if (a.IS_TOTAL) return -1;
  //   if (b.IS_TOTAL) return 1;
  //   if (sortOrder.value === "1") {
  //     // 상품명, 규격, 원산지순
  //     return (
  //       (a.CPUMNM || "").localeCompare(b.CPUMNM || "") ||
  //       (a.CSTDS || "").localeCompare(b.CSTDS || "") ||
  //       (a.CORIGINNM || "").localeCompare(b.CORIGINNM || "")
  //     );
  //   } else {
  //     // 상품명, 원산지, 규격순
  //     return (
  //       (a.CPUMNM || "").localeCompare(b.CPUMNM || "") ||
  //       (a.CORIGINNM || "").localeCompare(b.CORIGINNM || "") ||
  //       (a.CSTDS || "").localeCompare(b.CSTDS || "")
  //     );
  //   }
  // });

  // 서버 응답에 합계 행이 이미 포함되어 있는지 확인
  const isServerSummaryIncluded = list.some(
    (item) =>
      item.CPUMCD === "전 체" ||
      item.CJNAME === "전 체" ||
      (item.CPUMCD && (item.CPUMCD.includes("소계") || item.CPUMCD.includes("총계")))
  );

  let displayList = list;
  if (!isServerSummaryIncluded && list.length > 0) {
    const totalQty = list.reduce((acc, cur) => acc + (Number(cur.CJGMST) || 0), 0);
    const totalWt = list.reduce((acc, cur) => acc + (Number(cur.CJGJRT) || 0), 0);

    const totalRow = {
      CJNAME: "",
      CPUMCD: "전 체",
      CPUMNM: "",
      CJEJIL: "",
      CORIGINNM: "",
      CSTDS: "",
      CJGMST: totalQty,
      CJGJRT: totalWt,
      IS_TOTAL: true,
    };
    displayList = [totalRow, ...list];
  }

  arrGridData.value = displayList;
  totalResultCount.value = list.filter((item) => !item.IS_TOTAL && item.CPUMCD !== "전 체").length;
}

// 정렬 변경 이벤트
function onSortChange() {
  loadData();
}

// 조회 버튼 클릭
async function btnSearchClick() {
  await loadData();
  notify({
    title: "알림",
    text: "조회가 완료되었습니다.",
    type: "success",
  });
}

// 엑셀 다운로드 버튼 클릭
async function btnExcelClick() {
  if (arrGridData.value.length === 0) {
    notify({
      title: "경고",
      text: "다운로드할 데이터가 없습니다.",
      type: "warn",
    });
    return;
  }

  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("재고조회");

    // 컬럼 헤더 추가
    worksheet.columns = [
      { header: "재고처", key: "CJNAME", width: 14 },
      { header: "상품코드", key: "CPUMCD", width: 14 },
      { header: "상품명", key: "CPUMNM", width: 18 },
      { header: "재질", key: "CJEJIL", width: 14 },
      { header: "원산지", key: "CORIGINNM", width: 10 },
      { header: "규격", key: "CSTDS", width: 25 },
      { header: "재고매수", key: "CJGMST", width: 14 },
      { header: "재고중량", key: "CJGJRT", width: 16 },
    ];

    // 헤더 행 스타일 지정
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FF1E293B" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEBF2F8" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 24;

    // 데이터 추가
    arrGridData.value.forEach((item) => {
      const row = worksheet.addRow({
        CJNAME: item.CJNAME,
        CPUMCD: item.CPUMCD,
        CPUMNM: item.CPUMNM,
        CJEJIL: item.CJEJIL,
        CORIGINNM: item.CORIGINNM,
        CSTDS: item.CSTDS,
        CJGMST: Number(item.CJGMST) || 0,
        CJGJRT: Number(item.CJGJRT) || 0,
      });

      if (item.IS_TOTAL || item.CPUMCD === "전 체") {
        row.font = { bold: true, color: { argb: "FFB91C1C" } };
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFF5F5" },
        };
      }

      row.getCell("CJGMST").numFmt = "#,##0";
      row.getCell("CJGJRT").numFmt = "#,##0";
      row.alignment = { vertical: "middle" };
      row.getCell("CJNAME").alignment = { horizontal: "center" };
      row.getCell("CPUMCD").alignment = { horizontal: "center" };
      row.getCell("CJEJIL").alignment = { horizontal: "center" };
      row.getCell("CORIGINNM").alignment = { horizontal: "center" };
      row.getCell("CJGMST").alignment = { horizontal: "right" };
      row.getCell("CJGJRT").alignment = { horizontal: "right" };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `재고조회(영업사원용)_SM303_${new Date().toISOString().slice(0, 10)}.xlsx`
    );

    notify({
      title: "완료",
      text: "엑셀 다운로드가 완료되었습니다.",
      type: "success",
    });
  } catch (error) {
    console.error("엑셀 다운로드 에러:", error);
    notify({
      title: "오류",
      text: "엑셀 생성 중 오류가 발생했습니다.",
      type: "error",
    });
  }
}

// 행 선택 이벤트 핸들러
function handleSelection(_rows) {
  // 행 선택 시 필요에 따라 로직 확장
}
</script>

<style scoped>
/* Tabulator 헤더 및 셀 테두리/색상 보정 (ERP 스타일) */
:deep(.tabulator) {
  font-family: -apple-system, BlinkMacSystemFont, "Malgun Gothic", "맑은 고딕", helvetica, "Apple SD Gothic Neo", sans-serif;
  border: 1px solid #cbd5e1 !important;
}

:deep(.tabulator .tabulator-header) {
  background-color: #ebf2f8 !important;
  border-bottom: 2px solid #cbd5e1 !important;
  color: #1e3a5f !important;
  font-weight: 700 !important;
}

:deep(.tabulator .tabulator-header .tabulator-col) {
  background-color: #ebf2f8 !important;
  border-right: 1px solid #cbd5e1 !important;
}

:deep(.tabulator .tabulator-header .tabulator-col .tabulator-col-content) {
  padding: 6px 8px !important;
}

:deep(.tabulator .tabulator-row) {
  border-bottom: 1px solid #e2e8f0 !important;
}

:deep(.tabulator .tabulator-row .tabulator-cell) {
  padding: 4px 8px !important;
  font-size: 12px !important;
  border-right: 1px solid #e2e8f0 !important;
}

/* 폼 컨트롤 미세조정 */
select, input[type="text"] {
  box-sizing: border-box;
}
</style>
